export type Country = [code: string, label: string];

/**
 * Reorders a list of countries so that those matching the provided priority codes appear first, with no duplicates and case-insensitive matching.
 * Logs a warning for any priority codes not found in the country list.
 * @param {Country[]} allCountries - List of all countries as [code, label] tuples
 * @param {string[]} priorityCountryCodes - Array of country codes to prioritize (case-insensitive)
 * @returns {Country[]} New array with prioritized countries first, followed by the remaining ones
 */
export function prioritizeCountries(allCountries: Country[], priorityCountryCodes: string[]): Country[] {
  // Normalize and deduplicate priority codes (preserving order)
  const seen = new Set<string>();
  const uniquePriorityCodes = priorityCountryCodes
    .map((code) => code.toUpperCase())
    .filter((code) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    });

  // Create a map for fast, case-insensitive lookup of countries
  const countryMap = new Map(allCountries.map(([code, label]) => [code.toUpperCase(), [code, label] as Country]));

  const prioritized: Country[] = [];
  const notFound: string[] = [];

  // Build the prioritized list by looking up each code
  for (const code of uniquePriorityCodes) {
    const country = countryMap.get(code);

    if (country) prioritized.push(country);
    else notFound.push(code);
  }

  if (notFound.length > 0) {
    console.warn(`Warning: the following priority codes were not found: ${notFound.join(', ')}`);
  }

  // Filter out prioritized countries from the original list
  const priorityCodeSet = new Set(uniquePriorityCodes);
  const others = allCountries.filter(([code]) => !priorityCodeSet.has(code.toUpperCase()));

  return [...prioritized, ...others];
}
